import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { fmt } from "../format";

interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  createdAt: string;
  readAt: string | null;
}
interface Feed {
  notifications: Notification[];
  unread: number;
}

/**
 * The profile bell — a per-user notification feed (workspace invites accepted, invites claimed). Polls
 * the unread count, shows a badge, and on open lists recent notifications and marks them read. Rendered
 * beside the avatar in the top-right header for any signed-in user.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState<Feed | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      setFeed(await api<Feed>("/me/notifications"));
    } catch {
      /* transient / not authed — leave the last feed */
    }
  }

  useEffect(() => {
    void load();
    const iv = setInterval(() => void load(), 60_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    // Opening marks everything read + refreshes (so the badge clears once you've looked).
    if (next && (feed?.unread ?? 0) > 0) {
      try {
        await api("/me/notifications/read", { method: "POST" });
      } catch {
        /* ignore — the badge just stays until the next load */
      }
      void load();
    }
  }

  const unread = feed?.unread ?? 0;
  const items = feed?.notifications ?? [];

  return (
    <div className="notif-bell" ref={ref}>
      <button
        className="account-btn notif-btn"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        aria-expanded={open}
        onClick={() => void toggle()}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="notif-pop">
          <div className="notif-pop-head">Notifications</div>
          {items.length === 0 ? (
            <div className="notif-empty">You&rsquo;re all caught up.</div>
          ) : (
            <ul className="notif-list">
              {items.map((n) => (
                <li key={n.id} className={n.readAt ? "notif-item" : "notif-item unread"}>
                  <div className="notif-title">{n.title}</div>
                  {n.body && <div className="notif-body">{n.body}</div>}
                  <div className="notif-when">{fmt(n.createdAt)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
